import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ToastContainer, toast } from 'react-toastify';
import { FaEdit, FaTrash, FaTrashAlt } from 'react-icons/fa';
import {
  Container, Typography, Button, Card, CardContent, FormControl, Select, CircularProgress, MenuItem,
  InputAdornment, OutlinedInput, TextField, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Backdrop, Grid, IconButton, Divider,
  Autocomplete,
} from '@mui/material';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { addScheduleBulk, getShedule, updateShedule, getAllCourseDetails, getAllSubjectDetails, deleteSchedule, importAssignment, getAssignmentResult, } from '../Servies/AdmiServices';
import 'react-toastify/dist/ReactToastify.css';
import NewTableData from '../components/common/NewTableData';
import Loader from '../components/common/Loader';

// Raw teacher names as seen in past schedule data (kept as-is, including near-duplicate
// spellings) - deduped below into TEACHER_NAME_OPTIONS.
const RAW_TEACHER_NAMES = [
  'Manoj Sir', 'Gaurav', 'Pankaj', 'Simran Maam',  'Akshay Sir', 'Omkar',
   'Ramesh Sir', 'Ashish Sir', "Tiasha Ma'am", 'Sachin Sir', 'Prathmesh',
  'Pradeep', 'Vedant Sir', 'Datta Sir',   
  'Prathamesh Sir', 'Nimisha Maam', 'Jignesh Sir', 'Pankaj Sir',
   'Santosh Sir', 'Huzaifa Sir/Jaswinder Maam', 'Manoj Sir',
  "Dhanashree Ma'am", 'Bhavesh Sir',     
  "Manju Ma'am",  'Sunil Sir', 'Behram Sir',  'Huzaifa Sir',
   'Vaishnavi Maam',   'Ashwini Sir',
  'Anil Kumar Pandey Sir', 'Jaswinder maam', 'Sneh Sir', "Kavita Ma'am", 
  'Rohit Sir', "Kanchan Ma'am",
 'Ramesh Sir',  
  'Neha Maam',  'Sachin Salvi Sir', 'Ranjit Sir',
];

// Dedupe case/whitespace-insensitively (e.g. "Huzaifa Sir" / "Huzaifa SIr" / "Huzaifa  Sir" collapse
// to one entry) while keeping genuinely different spellings (e.g. "Jasvinder" vs "Jaswinder") distinct,
// since merging those would risk conflating two different teachers.
const TEACHER_NAME_OPTIONS = Array.from(
  new Map(
    RAW_TEACHER_NAMES.map((name) => name.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .map((name) => [name.toLowerCase(), name])
  ).values()
).sort((a, b) => a.localeCompare(b));

// Normalizes a preview column header ("Chapter Name", "chapter_name", ...) down to a bare
// lowercase key so it can be matched against SCHEDULE_FIELD_ALIASES regardless of how the
// uploaded sheet's header row is capitalized/spaced.
const normalizeHeaderKey = (header) => (header ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

// Maps a normalized preview header to a ScheduleDetails field via substring match, checked in
// order, so headers don't have to match one exact spelling (e.g. "Course Name", "Course Code",
// "Grade", "CourseId" all resolve to courseId as long as they contain one of these patterns).
const SCHEDULE_FIELD_PATTERNS = [
  ['courseid', 'courseId'],
  ['course', 'courseId'],
  ['grade', 'courseId'],
  ['subjectid', 'subjectId'],
  ['subject', 'subjectId'],
  ['schedulename', 'scheduleName'],
  ['chapter', 'chapterName'],
  ['topic', 'topicsToBePrepared'],
  ['teacher', 'teacherName'],
  ['duration', 'duration'],
  ['starttime', 'startTime'],
  ['startdate', 'startTime'],
  ['link', 'url'],
  ['url', 'url'],
  ['prework', 'preWork'],
  ['classwork', 'classWork'],
  ['postwork', 'postWork'],
];

// Some templates don't have one combined "StartTime" column - they split it into separate
// Date/Month/Year/Time columns (matching the same 4 cells the old Excel-import path on the
// backend concatenates - see column indexes 10-13 in ScheduleServiceImpl.updateSchedule).
// These are matched by exact header text (not substring) since "Date"/"Month"/"Year"/"Time"
// are too generic to safely substring-match against other columns. Note: "Date" (day-of-month,
// e.g. "27") is intentionally NOT the same as a separate "Day" column some templates also have
// (day-of-week, e.g. "Thursday") - only "Date" feeds the combined startTime.
const DATE_PART_HEADER_KEYS = { date: 'day', month: 'month', year: 'year', time: 'time' };

const resolveScheduleField = (header) => {
  const normalized = normalizeHeaderKey(header);
  const match = SCHEDULE_FIELD_PATTERNS.find(([pattern]) => normalized.includes(pattern));
  return match ? match[1] : null;
};

// The old Excel-import path stores Course/Subject cells as "DisplayName_id" and keeps only
// the id after the last underscore (see ScheduleServiceImpl.updateSchedule on the backend) -
// replicate that so composite values from the same template still resolve to a plain id.
const extractTrailingId = (value) => {
  const stringValue = (value ?? '').toString();
  const parts = stringValue.split('_');
  return parts[parts.length - 1].trim();
};

// Combines separate Date ("27") / Month ("August") / Year ("2026") / Time ("6:30:00 PM")
// column values into the "YYYY-MM-DD HH:mm:ss" string the backend's @JsonFormat expects.
const combineDateParts = ({ day, month, year, time }) => {
  if (!day || !month || !year || !time) return null;
  const combined = `${day} ${month} ${year} ${time}`.trim();
  const parsedDate = moment(
    combined,
    ['D MMMM YYYY h:mm:ss A', 'D MMM YYYY h:mm:ss A', 'D M YYYY h:mm:ss A', 'D MMMM YYYY HH:mm:ss', 'D MMM YYYY HH:mm:ss'],
    false
  );
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD HH:mm:ss') : null;
};

const mapPreviewRowToScheduleDetails = (row, unmappedHeaders) => {
  const scheduleDetails = {};
  const dateParts = {};

  Object.entries(row).forEach(([header, value]) => {
    const field = resolveScheduleField(header);
    if (field) {
      if (field === 'courseId') {
        scheduleDetails.courseId = extractTrailingId(value);
      } else if (field === 'subjectId') {
        const subjectId = extractTrailingId(value);
        scheduleDetails.subjectId = subjectId === '' ? null : Number(subjectId);
      } else if (field === 'duration') {
        scheduleDetails.duration = value === '' ? null : Number(value);
      } else if (field === 'startTime') {
        const parsedDate = moment(value, ['YYYY-MM-DD HH:mm:ss', 'DD-MMM-YYYY hh:mm:ss', moment.ISO_8601], true);
        scheduleDetails.startTime = parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD HH:mm:ss') : value;
      } else {
        scheduleDetails[field] = value;
      }
      return;
    }

    const datePartKey = DATE_PART_HEADER_KEYS[normalizeHeaderKey(header)];
    if (datePartKey) {
      dateParts[datePartKey] = (value ?? '').toString().trim();
    } else {
      unmappedHeaders?.add(header);
    }
  });

  if (!scheduleDetails.startTime) {
    const combinedStartTime = combineDateParts(dateParts);
    if (combinedStartTime) {
      scheduleDetails.startTime = combinedStartTime;
    }
  }

  return scheduleDetails;
};

export default function Scheduler() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showTable, setShowTable] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  // const [pageSize, setPageSize] = useState(20);
  const [totalRecord,setTotalRecord]=useState(0)
  const [isLoading, setIsLoading] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [editingRow, setEditingRow] = useState(null)
  const [editedData, setEditedData] = useState({});
  const [Courses, setCourse] = useState([]);
  const [subject, setSubject] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [uploadedFileAs, setUploadedFileAs] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryRows, setManualEntryRows] = useState([]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const newPageSize = 10;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  const onSelectCourse = (event) => {
    const selectedCourseObject = Courses.find((course) => course.courseName === event.target.value);
    setSelectedCourse(selectedCourseObject);
    setEditedData({ ...editedData, courseId: selectedCourseObject.courseId });

    if (selectedCourseObject) {
      fetchSubjectData(selectedCourseObject.courseId);
    }
  };

  const onSelectSubject = (event) => {
    const selectedSubjectObject = subject.find((subject) => subject.subjectName === event.target.value);
    setSelectedSubject(selectedSubjectObject);
    setEditedData({ ...editedData, subjectId: selectedSubjectObject.levelId });
  };
  console.log('check subject', selectedSubject);
  const handleChangeCourseList = async () => {
    const courseDetails = await getAllCourseDetails();
    const courseCode = courseDetails?.data?.map((data) => ({
      courseName: data?.courseDisplayName,
      courseId: data?.courseId,
    }));
    setCourse(courseCode);
  };
  useEffect(() => {
    handleChangeCourseList();
  }, []);

  const fetchSubjectData = async (courseId) => {
    try {
      const subjectDetails = await getAllSubjectDetails(courseId);

      const subjectCode = subjectDetails?.data?.map((data) => ({
        subjectName: data?.levelDisplayName,
        levelId: data?.levelId,
      }));

      setSubject(subjectCode);
    } catch (error) {
      console.error('Error fetching subject details:', error);
    }
  };

  useEffect(() => {
    if (selectedCourse) {
      const selectedCourseObject = Courses.find((course) => course.courseName === selectedCourse);
      if (selectedCourseObject) {
        fetchSubjectData(selectedCourseObject.courseId);
      }
    }
  }, [selectedCourse, Courses]);

  const handleReportClick = (id) => {
    navigate(`/dashboard/AssignmentResult?id=${id}`);
    console.log(id, 'check iddd');
  };

  const fetchData = () => {
    console.log("pageNo:: ", pageNo);
    getShedule(pageNo, 20)
      .then((response) => {
        const scheduleData = response.data.data;
        setTotalRecord(response.data.totalPage)
        setShowTable(scheduleData);
        setFilteredData(scheduleData)
        const ids = scheduleData.map((item) => item.id);
        console.log(ids);
        toast.success(response?.data?.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };
  useEffect(() => {
    fetchData();
    console.log("Init");
  }, []);

  const handleNextPage = () => {
    setPageNo(pageNo+1)
  };

  const handlePrevPage = () => {
    if (pageNo > 1) {
      setPageNo(pageNo - 1);
    }
  };
  
  useEffect(()=>{
    fetchData()
    console.log("Page");
  },[pageNo]);
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadedFile(file);
    setIsParsingFile(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw: false renders each cell using its Excel display format (dates/times included)
        // instead of converting them to serial numbers or re-parsed JS Date objects.
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
        const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false })[0] || [];

        setPreviewHeaders(headerRow);
        setPreviewRows(rows);
        setShowPreviewDialog(true);
      } catch (error) {
        toast.error('Could not read this file as an Excel sheet. Please upload a .xls/.xlsx file.', {
          position: toast.POSITION.TOP_RIGHT,
        });
        setUploadedFile(null);
      } finally {
        setIsParsingFile(false);
      }
    };
    reader.onerror = () => {
      toast.error('Could not read this file. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      setUploadedFile(null);
      setIsParsingFile(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUploadAs = (event, id) => {
    const file = event.target.files[0];
    setUploadedFileAs({ file, id });
  };

  const handlePreviewCellChange = (rowIndex, header, value) => {
    setPreviewRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [header]: value };
      return updated;
    });
  };

  const handleRemovePreviewRow = (rowIndex) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const handleCancelPreview = (event) => {
    setShowPreviewDialog(false);
    setUploadedFile(null);
    setPreviewHeaders([]);
    setPreviewRows([]);
    if (event?.target) {
      event.target.value = '';
    }
  };

  const handleConfirmPreview = () => {
    const unmappedHeaders = new Set();
    const scheduleRows = previewRows.map((row) => mapPreviewRowToScheduleDetails(row, unmappedHeaders));

    if (unmappedHeaders.size > 0) {
      console.warn('Schedule preview: these columns were not recognized and were not sent:', [...unmappedHeaders]);
    }

    const courseIdHeader = previewHeaders.find((header) => resolveScheduleField(header) === 'courseId');
    if (!courseIdHeader) {
      toast.error(
        `Could not find a "Course"/"Grade" column to read courseId from. Columns found: ${previewHeaders.join(', ')}`,
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }
    const rowsMissingCourseId = scheduleRows.reduce((rowNumbers, row, index) => {
      if (!row.courseId) rowNumbers.push(index + 1);
      return rowNumbers;
    }, []);
    if (rowsMissingCourseId.length > 0) {
      toast.error(
        `Row ${rowsMissingCourseId.join(', ')} ${rowsMissingCourseId.length > 1 ? 'have' : 'has'} an empty "${courseIdHeader}" value. Fix or remove ${rowsMissingCourseId.length > 1 ? 'those rows' : 'that row'} in the preview before confirming.`,
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }

    setShowPreviewDialog(false);
    setIsLoading(true);
    addScheduleBulk(scheduleRows)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.success === true) {
          setUploadedFile(null);
          setPreviewHeaders([]);
          setPreviewRows([]);
          fetchData();
          toast.success(res?.data?.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error(res?.data?.message || 'Failed to add schedule details.', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Add schedule bulk error:', error);
        toast.error('Failed to add schedule details.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const createEmptyManualEntryRow = () => ({
    key: `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    courseId: '',
    subjectId: '',
    subjectOptions: [],
    scheduleName: '',
    chapterName: '',
    topicsToBePrepared: '',
    teacherName: '',
    duration: '',
    startTime: '',
    url: '',
    preWork: '',
    classWork: '',
    postWork: '',
  });

  const handleOpenManualEntry = () => {
    setManualEntryRows([createEmptyManualEntryRow()]);
    setShowManualEntryDialog(true);
  };

  const handleCancelManualEntry = () => {
    setShowManualEntryDialog(false);
    setManualEntryRows([]);
  };

  const handleAddManualRow = () => {
    setManualEntryRows((prev) => [...prev, createEmptyManualEntryRow()]);
  };

  const handleRemoveManualRow = (key) => {
    setManualEntryRows((prev) => prev.filter((row) => row.key !== key));
  };

  const handleManualFieldChange = (key, field, value) => {
    setManualEntryRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const handleManualCourseChange = async (key, courseId) => {
    setManualEntryRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, courseId, subjectId: '', subjectOptions: [] } : row))
    );
    try {
      const subjectDetails = await getAllSubjectDetails(courseId);
      const subjectOptions =
        subjectDetails?.data?.map((data) => ({ subjectName: data?.levelDisplayName, levelId: data?.levelId })) || [];
      setManualEntryRows((prev) => prev.map((row) => (row.key === key ? { ...row, subjectOptions } : row)));
    } catch (error) {
      console.error('Error fetching subject details:', error);
    }
  };

  const handleSubmitManualEntries = () => {
    const invalidRowNumbers = manualEntryRows.reduce((rowNumbers, row, index) => {
      if (!row.courseId || !row.subjectId || !row.scheduleName) rowNumbers.push(index + 1);
      return rowNumbers;
    }, []);
    if (invalidRowNumbers.length > 0) {
      toast.error(`Row ${invalidRowNumbers.join(', ')}: Course, Subject and Schedule Name are required.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const scheduleRows = manualEntryRows.map((row) => ({
      courseId: row.courseId,
      subjectId: Number(row.subjectId),
      scheduleName: row.scheduleName,
      chapterName: row.chapterName,
      topicsToBePrepared: row.topicsToBePrepared,
      teacherName: row.teacherName,
      duration: row.duration === '' ? null : Number(row.duration),
      startTime: row.startTime ? moment(row.startTime, 'YYYY-MM-DDTHH:mm', true).format('YYYY-MM-DD HH:mm:ss') : null,
      url: row.url,
      preWork: row.preWork,
      classWork: row.classWork,
      postWork: row.postWork,
    }));

    setIsLoading(true);
    addScheduleBulk(scheduleRows)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.success === true) {
          setShowManualEntryDialog(false);
          setManualEntryRows([]);
          fetchData();
          toast.success(res?.data?.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error(res?.data?.message || 'Failed to add schedule details.', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Add schedule manual entry error:', error);
        toast.error('Failed to add schedule details.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const handleAssignmentUpload = () => {
    const { file, id } = uploadedFileAs;
    setIsLoading(true);
    importAssignment(id, file)
      .then((res) => {
        if (res?.data.success === true) {
          setIsLoading(false);
          fetchData();
        } else {
          setIsLoading(false);
          toast.success(res?.data?.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('AddCourse error:', error);
      });
  };

  const handleEditClick = (rowData) => {
    setEditingRow(rowData);
    setEditedData({ ...rowData }); // Store the original data
  };
  const handleDeleteSchedule = (id) => {
    setIsLoading(true);

    deleteSchedule(id).then((res) => {
      console.log(res?.data?.data);
      if (res?.data.SUCCESS === true) {
        toast.success(res?.data?.Data, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setIsLoading(false);
        fetchData();
      } else {
        setIsLoading(false);
      }
    });
  };

  const handleSaveClick = () => {
    console.log(editedData)
    updateShedule(editedData)
      .then((res) => {
        if (res?.status === 'FAILED') {
          setIsLoading(false);
          toast.error(res?.data?.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          setIsLoading(false);
          setEditingRow(null);
          toast.success('Add schedule details successfully', {
            position: toast.POSITION.TOP_RIGHT,
          });
          fetchData();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Update Schedule error:', error);
      });
  };

  // useEffect(() => {
  //   if (isLoading === true) {
  //     fetchData();
  //     setIsLoading(false);
  //   }
  //   console.log("Loading");
  // }, [isLoading]);

  const headers = ['Course', 'Subject', 'ScheduleName', 'Chapter Name', 'Topics to be prepared',
    // 'ClassWork', // 'PostWork', 
    'TeacherName', 'Duration', 'StartTime', 'Link', 'Status', 'Action',];

  const specificColumns = Array.isArray(filteredData)
    ? filteredData.map((item) => {
      const isEditing = editingRow && editingRow.id === item.id;

      return {
        Course: isEditing ? (
          <Select type="text" sx={{ m: 1, width: 200 }} size={'small'}
            value={selectedCourse ? selectedCourse.courseName : '' || item.courseDisplayName}
            onChange={onSelectCourse}
            startAdornment={
              <InputAdornment position="start">
                {isLoading === true ? <CircularProgress size={15} /> : ''}
              </InputAdornment>
            }
            MenuProps={MenuProps}
          >
            {Courses.map((course) => (
              <MenuItem key={course.courseId} value={course.courseName}>
                {course.courseName}
              </MenuItem>
            ))}
          </Select>
        ) : (
          item.courseDisplayName
        ),
        Subject: isEditing ? (
          <Select type="text" sx={{ m: 1, width: 200 }} size={'small'}
            value={(selectedSubject ? selectedSubject.subjectName : '') || item.subjectDisplayName}
            onChange={onSelectSubject}
            startAdornment={
              <InputAdornment position="start">
                {isLoading === true ? <CircularProgress size={15} /> : ''}
              </InputAdornment>
            }
            MenuProps={MenuProps}
          >
            {subject.map((subject) => (
              <MenuItem key={subject.levelId} value={subject.subjectName}>
                {subject.subjectName}
              </MenuItem>
            ))}
          </Select>
        ) : (
          item.subjectDisplayName
        ),

        ScheduleName: isEditing ? (
          <TextField type="text" sx={{ m: 1, width: 200 }}
            value={editedData.scheduleName || ''}
            onChange={(e) => setEditedData({ ...editedData, scheduleName: e.target.value })}
          />
        ) : (
          item.scheduleName
        ),
        'Chapter Name': isEditing ? (
          <TextField type="text" sx={{ m: 1, width: 200 }}
            value={editedData.chapterName || ''}
            onChange={(e) => setEditedData({ ...editedData, chapterName: e.target.value })}
          />
        ) : (
          item.chapterName
        ),

        'Topics to be prepared': isEditing ? (
          <TextField type="text" sx={{ m: 1, width: 200 }}
            value={editedData.topicsToBePrepared || ''}
            onChange={(e) => setEditedData({ ...editedData, topicsToBePrepared: e.target.value })}
          />
        ) : (
          <Tooltip title={item.topicsToBePrepared}>
            <p style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', }}>{item.topicsToBePrepared}</p>
          </Tooltip>
        ),

        // ClassWork: isEditing ? (
        //   <TextField
        //     type="text"
        //     sx={{ m: 1, width: 200 }}
        //     value={editedData.classWork || ''}
        //     onChange={(e) => setEditedData({ ...editedData, ClassWork: e.target.value })}
        //   />
        // ) : (
        //   item.classWork
        // ),
        // PostWork: isEditing ? (
        //   <TextField
        //     type="text"
        //     sx={{ m: 1, width: 200 }}
        //     value={editedData.postWork || ''}
        //     onChange={(e) => setEditedData({ ...editedData, PostWork: e.target.value })}
        //   />
        // ) : (
        //   item.postWork
        // ),
        TeacherName: isEditing ? (
          <TextField
            type="text"
            sx={{ m: 1, width: 200 }}
            value={editedData.teacherName || ''}
            onChange={(e) => setEditedData({ ...editedData, teacherName: e.target.value })}
          />
        ) : (
          item.teacherName
        ),

        StartTime: isEditing ? (
          <TextField
            type="datetime-local"
            sx={{ m: 1, width: 200 }}
            value={editedData.startTime || ''}
            // onChange={(e) => setEditedData({ ...editedData, startTime: e.target.value })}
            onChange={(e) => {
              const date = new Date(e.target.value);
              const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:00`;
              setEditedData({ ...editedData, startTime: formattedDate });
            }}
          />
        ) : (
          item.startTime
        ),
        Duration: isEditing ? (
          <TextField
            type="number"
            sx={{ m: 1, width: 200 }}
            value={editedData.duration || 0}
            onChange={(e) => setEditedData({ ...editedData, duration: e.target.value })}
          />
        ) : (
          item.duration
        ),
        Link: isEditing ? (
          <TextField
            type="text"
            sx={{ m: 1, width: 200 }}
            value={editedData.url || ''}
            onChange={(e) => setEditedData({ ...editedData, url: e.target.value })}
          />
        ) : (
          item.url
        ),
        Status: item.isActive ? (
          <Button variant="contained" color="primary">
            Active
          </Button>
        ) : (
          'Inactive'
        ),
        Action: isEditing ? (
          <div>
            <Button variant="contained" color="primary" onClick={handleSaveClick}>
              Save
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyItems: 'center', alignItems: 'center', gap: '2rem' }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={(event) => handleFileUploadAs(event, item.id)}
              style={{ marginTop: '10px', marginBottom: '10px' }}
            />
            {/* <IconButton color="secondary" onClick={() => uploadedFileAs(null)}>
                <FaTrashAlt />
              </IconButton> */}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAssignmentUpload}
              style={{ marginTop: '10px', marginBottom: '10px' }}
            >
              Upload
            </Button>
            <Button variant="contained" color="primary" onClick={() => handleReportClick(item.id)}>
              Report
            </Button>
            <FaEdit style={{ cursor: 'pointer', marginRight: '10px' }} onClick={() => handleEditClick(item)} />
            <FaTrash style={{ cursor: 'pointer' }} onClick={() => handleDeleteSchedule(item.id)} />
          </div>
        ),
      };
    })
    : [];


  const handleSearch = (event) => {
    event.preventDefault();

    const searchData = showTable.filter((item) => {
      return item.scheduleName?.toLowerCase().includes(search.toLowerCase()) ||
        item?.chapterName?.toLowerCase().includes(search.toLowerCase()) ||
        item?.topicsToBePrepared?.toLowerCase().includes(search.toLowerCase()) ||
        item?.teacherName?.toLowerCase().includes(search.toLowerCase()) ||
        item?.courseDisplayName?.toLowerCase().includes(search.toLowerCase())
    });

    setFilteredData(searchData);
  };


  return (
    <>
      {isLoading && <Loader />}
      <Backdrop open={isParsingFile} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography variant="body1">Reading {uploadedFile?.name || 'file'}…</Typography>
        </Stack>
      </Backdrop>
      <ToastContainer />
      <Helmet>
        <title> Dashboard: Scheduler </title>
      </Helmet>

      <Container>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Scheduler
        </Typography>

        <Card style={{ width: '100%', maxWidth: '100%' }}>
          <CardContent
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label htmlFor="containedfile">
                <Button variant="contained" color="primary" component="span" id="contained-button-file">
                  Import
                </Button>

                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  style={{ display: 'none' }}
                  id="containedfile"
                  onChange={handleFileUpload}
                />
              </label>
              {uploadedFile && (
                <Typography variant="body1" style={{ marginLeft: '10px' }}>
                  {uploadedFile.name}
                </Typography>
              )}
              <Button variant="outlined" color="primary" sx={{ ml: 2 }} onClick={handleOpenManualEntry}>
                Add Manually
              </Button>
            </div>
          </CardContent>

          <br />
          <Container>
            <form onSubmit={handleSearch} style={{ marginBottom: '20px' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField name="search" label="Search"
                  variant="outlined" style={{ width: 320 }}
                  onChange={(e) => { setSearch(e.target.value) }}
                />
                <Button type="submit" variant="contained" style={{ height: '40px' }}> Search </Button>
              </Stack>
            </form>
          </Container>
          <NewTableData data={specificColumns} headers={headers} currentPage={pageNo}  totalPages={totalRecord} rowsPerPageOptions={[20]} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
            <Button variant="contained" color="primary" onClick={handlePrevPage} disabled={pageNo === 1}>
              Previous
            </Button>
            <Button variant="contained" color="primary" onClick={handleNextPage}>
              Next
            </Button>
          </div>
        </Card>
      </Container>

      <Dialog open={showPreviewDialog} onClose={handleCancelPreview} maxWidth="xl" fullWidth>
        <DialogTitle>Review data before adding to database</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {previewRows.length} row(s) found in {uploadedFile?.name}. Fix any incorrect values, or remove rows you
            don't want added, before confirming.
          </Typography>
          <TableContainer component={Paper} style={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  {previewHeaders.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell>{rowIndex + 1}</TableCell>
                    {previewHeaders.map((header) => (
                      <TableCell key={header}>
                        <TextField
                          variant="standard"
                          value={row[header] ?? ''}
                          onChange={(e) => handlePreviewCellChange(rowIndex, header, e.target.value)}
                          sx={{ minWidth: 140 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <FaTrash
                        style={{ cursor: 'pointer' }}
                        title="Remove this row"
                        onClick={() => handleRemovePreviewRow(rowIndex)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPreview}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmPreview} disabled={previewRows.length === 0}>
            Confirm &amp; Add to Database
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManualEntryDialog} onClose={handleCancelManualEntry} maxWidth="lg" fullWidth>
        <DialogTitle>Add Schedule Manually</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add one or more schedule entries without an Excel file. Course and Subject are required to look up their
            ids; the rest is optional.
          </Typography>
          <Stack spacing={2} sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
            {manualEntryRows.map((row, index) => (
              <Card key={row.key} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Entry {index + 1}</Typography>
                    <IconButton
                      size="small"
                      title="Remove this entry"
                      onClick={() => handleRemoveManualRow(row.key)}
                      disabled={manualEntryRows.length === 1}
                    >
                      <FaTrash />
                    </IconButton>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={row.courseId}
                          onChange={(e) => handleManualCourseChange(row.key, e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Select Course
                          </MenuItem>
                          {Courses.map((course) => (
                            <MenuItem key={course.courseId} value={course.courseId}>
                              {course.courseName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={row.subjectId}
                          onChange={(e) => handleManualFieldChange(row.key, 'subjectId', e.target.value)}
                          disabled={!row.courseId}
                        >
                          <MenuItem value="" disabled>
                            Select Subject
                          </MenuItem>
                          {row.subjectOptions.map((subjectOption) => (
                            <MenuItem key={subjectOption.levelId} value={subjectOption.levelId}>
                              {subjectOption.subjectName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Schedule Name"
                        value={row.scheduleName}
                        onChange={(e) => handleManualFieldChange(row.key, 'scheduleName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        size="small"
                        options={TEACHER_NAME_OPTIONS}
                        value={row.teacherName}
                        onChange={(e, newValue) => handleManualFieldChange(row.key, 'teacherName', newValue || '')}
                        onInputChange={(e, newInputValue) => handleManualFieldChange(row.key, 'teacherName', newInputValue)}
                        renderInput={(params) => <TextField {...params} label="Teacher Name" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Duration (minutes)"
                        value={row.duration}
                        onChange={(e) => handleManualFieldChange(row.key, 'duration', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        label="Start Time"
                        InputLabelProps={{ shrink: true }}
                        value={row.startTime}
                        onChange={(e) => handleManualFieldChange(row.key, 'startTime', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link"
                        value={row.url}
                        onChange={(e) => handleManualFieldChange(row.key, 'url', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Chapter Name"
                        value={row.chapterName}
                        onChange={(e) => handleManualFieldChange(row.key, 'chapterName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        label="Topics to be Prepared"
                        value={row.topicsToBePrepared}
                        onChange={(e) => handleManualFieldChange(row.key, 'topicsToBePrepared', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Pre Work"
                        value={row.preWork}
                        onChange={(e) => handleManualFieldChange(row.key, 'preWork', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Class Work"
                        value={row.classWork}
                        onChange={(e) => handleManualFieldChange(row.key, 'classWork', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Post Work"
                        value={row.postWork}
                        onChange={(e) => handleManualFieldChange(row.key, 'postWork', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Button sx={{ mt: 2 }} onClick={handleAddManualRow}>
            + Add Another Entry
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelManualEntry}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmitManualEntries}>
            Add {manualEntryRows.length > 1 ? `${manualEntryRows.length} Entries` : 'Entry'} to Database
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
